import {
  ActivityLogKinds,
  CategoryActivityLog,
} from "@converge-collective/common/models/ActivityLog";
import {
  ActivityChallenge,
  Challenge,
  ChallengeCategory,
  ChallengeKinds,
} from "@converge-collective/common/models/Challenge";
import { SubCategoryWithCategory } from "@converge-collective/common/models/SubCategory";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Dialog,
  Paper,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { DateTimePicker } from "@mui/x-date-pickers";
import { startOfHour } from "date-fns";
import { isEmpty, isNaN, maxBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useFirestore, useFirestoreCollectionData, useUser } from "reactfire";
import { useCurrentChallenges } from "~/hooks/useProfile";
import AutocompleteCategory from "./AutocompleteCategory";
import { WithID } from "@converge-collective/common/models/Base";

const MaxDurationMinutes = 60 * 30; // 30 hours max duration for ultra event

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
  },
  dialog: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // description: {
  //   marginRight: 12,
  //   marginBottom: 20,
  //   width: 220,
  // },
}));

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  handleActivityAdded: () => void;
};

const prevHour = () => startOfHour(new Date());

export default function LogActivity({
  isOpen,
  handleClose,
  handleActivityAdded,
}: Props): React.ReactElement {
  const classes = useStyles();
  const firestore = useFirestore();
  const catRef = firestore.collection("categories");
  const { data: user } = useUser();
  const userChallenges = useCurrentChallenges(user?.uid);
  const { data: categories } = useFirestoreCollectionData(catRef, {
    idField: "id",
  });

  const defaultDuration = 30;
  const [duration, setDuration] = useState(defaultDuration);
  const [description, setDescription] = useState("");

  useEffect(() => {
    // when the user opens the dialog, update the date to the previous hour
    setDate(prevHour());
    console.log("LogActivity update date", { date: prevHour() });
  }, [isOpen]);

  const [subCategory, setSubCategory] =
    useState<SubCategoryWithCategory | null>(null);

  const [date, setDate] = useState(prevHour());

  const resetForm = () => {
    setIsLoading(false);
    setValidDuration(true);
    setValidCategory(true);
    setDuration(defaultDuration);
    setDescription("");
    setSubCategory(null);
    setDate(prevHour());
  };

  const loading = !categories;

  const [validDuration, setValidDuration] = useState(true);
  const [durationError, setDurationError] = useState("");
  const [validCategory, setValidCategory] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const validateDuration = () => {
    if (isNaN(duration)) {
      setValidDuration(false);
      setDurationError("Please enter the number of minutes you logged!");
    } else if (duration > MaxDurationMinutes) {
      setValidDuration(false);
      setDurationError(
        "Duration cannot be greater than 30 hours (1800 minutes)"
      );
    } else {
      setValidDuration(true);
      setDurationError("");
    }
  };
  const validateCategory = (value = subCategory) => {
    setValidCategory(!isEmpty(value));
  };

  type ChallengeCategoryWithChallenge = WithID<ChallengeCategory> & {
    challenge: Challenge;
  };

  const getExtraMultiplier = (
    category: string
  ): ChallengeCategoryWithChallenge | undefined =>
    maxBy(
      userChallenges &&
        userChallenges
          .filter(
            (c): c is WithID<ActivityChallenge> =>
              c.kind === ChallengeKinds.ActivityChallenge
          )
          .flatMap((c: WithID<ActivityChallenge>) =>
            c.categories.map((cc) => ({ ...cc, challenge: c }))
          )
          .filter(
            (c: ChallengeCategoryWithChallenge) => c.category == category
          ),
      "multiplier"
    );

  const handleSubmit = async () => {
    validateDuration();
    validateCategory();

    if (validDuration && validCategory && subCategory) {
      setIsLoading(true);
      // find the active challenges for the use
      const maxChallengeMultiplier = getExtraMultiplier(
        subCategory.subCategory.subCategory
      );

      const activityLog: CategoryActivityLog = {
        kind: ActivityLogKinds.CategoryActivityLog,
        uid: user.uid,
        date,
        categoryId: subCategory.subCategory.id,
        categoryName: subCategory.subCategory.subCategory,
        // if we didn't specify points for a category, default to 0
        categoryBaseMultiplier: subCategory.subCategory.points ?? 0,
        duration,
        description,
        ...(maxChallengeMultiplier && {
          extraMultiplier: maxChallengeMultiplier?.multiplier,
          extraMultiplierChallenge: maxChallengeMultiplier?.challenge,
        }),
      };

      await firestore
        .collection("profiles")
        .doc(user.uid)
        .collection("activityLogs")
        .add(activityLog);

      handleActivityAdded();
      handleClose();
      resetForm();
    } else {
      console.warn("invalid data", {
        validDuration,
        validCategory,
        subCategory,
      });
    }
  };

  const localHandlClose = () => {
    resetForm();
    handleClose();
  };

  const shouldDisableDate = (date: Date) => date > new Date();

  return (
    <>
      <Dialog
        className={classes.dialog}
        open={isOpen}
        onClose={localHandlClose}
        closeAfterTransition
        maxWidth="lg"
      >
        <Paper className={classes.paper}>
          <Typography sx={{ mb: 4 }} variant="h4" color="primary">
            Log Activity
          </Typography>

          {!loading && (
            <>
              <DateTimePicker
                label="Date and time"
                minutesStep={5}
                shouldDisableDate={shouldDisableDate}
                value={date}
                onChange={(d: Date) => setDate(d)}
                slotProps={{
                  textField: { fullWidth: true },
                }}
              />

              <TextField
                value={description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setDescription(event.target.value);
                }}
                fullWidth
                sx={{ my: 2 }}
                label="Description"
              />
              <TextField
                type="number"
                value={duration}
                error={!validDuration}
                helperText={<>{isEmpty(durationError) || durationError}</>}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setDuration(parseInt(event.target.value));
                  validateDuration();
                }}
                onBlur={validateDuration}
                label="Minutes"
                fullWidth
                sx={{ my: 1 }}
              />

              <Box mt={1}>
                <AutocompleteCategory
                  filterZeroPointCats={true}
                  onSetSubCategories={(subCats) => {
                    if (!isEmpty(subCats)) {
                      const [subCat] = subCats;
                      setSubCategory(subCat);
                      validateCategory(subCat);
                    }
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end">
                <LoadingButton
                  loading={isLoading}
                  onClick={handleSubmit}
                  style={{ marginTop: 10, marginLeft: 12 }}
                  color="secondary"
                  variant="contained"
                  startIcon={<EmojiEventsIcon />}
                >
                  Add
                </LoadingButton>
              </Box>
            </>
          )}
        </Paper>
      </Dialog>
    </>
  );
}
